import AbstractCustomerValueModel from '../abstract_customer_value_model';
import BenefitsChartValue from '../../charts/benefits_chart_value';
import BenefitsWaterfallChart from '../../charts/benefits_waterfall_chart';

class PmbEnterprise extends AbstractCustomerValueModel {
  custom_chart_classes() {
    return { BenefitsChartValue, BenefitsWaterfallChart };
  }

  // Impact Variables
  percent_reduction_in_time_spent_managing_billing() {
    return this.input.percent_reduction_in_time_spent_managing_billing / 100;
  }

  percent_reduction_in_time_spent_on_payment_collection() {
    return this.input.percent_reduction_in_time_spent_on_payment_collection / 100;
  }

  percent_reduction_in_time_spent_on_payment_reconciliation() {
    return this.input.percent_reduction_in_time_spent_on_payment_reconciliation / 100;
  }

  percent_reduction_in_time_spent_on_processing_refunds() {
    return this.input.percent_reduction_in_time_spent_on_processing_refunds / 100;
  }

  percent_reduction_in_time_spent_on_new_sites() {
    return this.input.percent_reduction_in_time_spent_on_new_sites / 100;
  }

  percent_reduction_in_cost_of_merchant_services() {
    return this.input.percent_reduction_in_cost_of_merchant_services / 100;
  }

  systems_displaced_by_pmb() {
    return this.input.systems_displaced_by_pmb;
  }

  percent_reduction_in_compliance_fees() {
    return this.input.percent_reduction_in_compliance_fees / 100;
  }

  percentage_point_improvement_in_collection() {
    return this.input.percentage_point_improvement_in_collection / 100;
  }

  // Benchmark Inputs
  annual_billing_staff_salary() { // K23
    return this.input.annual_billing_staff_salary;
  }

  annual_payment_collecting_staff_salary() { // K27
    return this.input.annual_payment_collecting_staff_salary;
  }

  annual_payment_reconciliation_staff_salary() { // K31
    return this.input.annual_payment_reconciliation_staff_salary;
  }

  annual_refund_processing_staff_salary() { // K35
    return this.input.annual_refund_processing_staff_salary;
  }

  annual_new_site_staff_salary() { // K40
    return this.input.annual_new_site_staff_salary;
  }

  time_spent_per_new_site() { // K38
    return this.input.time_spent_per_new_site;
  }

  spend_per_system() { // K45
    return this.input.spend_per_system;
  }

  pci_compliance_fees_per_office() { // K47
    return this.input.pci_compliance_fees_per_office;
  }

  nonpci_compliance_fees_per_office() { // K48
    return this.input.nonpci_compliance_fees_per_office;
  }

  // Configurator - K67
  burden_rate() {
    return this.input.burden_rate;
  }

  // Derived Inputs
  fully_burdened_salary(salary) {
    return salary * this.burden_rate();
  }

  dollar_value_of_time_saved() { // ROI Analysis S44
    return this.reduce_time_managing_patient_billing()
      + this.reduce_time_collecting_posting_payment()
      + this.reduce_time_reconciling_payment()
      + this.reduce_time_processing_refunds()
      + this.reduce_time_on_new_sites();
  }

  hourly_blended_salary() { // ROI Analysis S48
    return (this.time_spent_managing_billing() / this.total_hours() * this.fully_burdened_salary(this.annual_billing_staff_salary())
      + this.time_spent_on_payment_collection() / this.total_hours() * this.fully_burdened_salary(this.annual_payment_collecting_staff_salary())
      + this.time_spent_on_payment_reconciliation() / this.total_hours() * this.fully_burdened_salary(this.annual_payment_reconciliation_staff_salary())
      + this.time_spent_processing_refunds() / this.total_hours() * this.fully_burdened_salary(this.annual_refund_processing_staff_salary())
      + (this.time_spent_per_new_site() * this.new_sites_per_year()) / this.total_hours() * this.fully_burdened_salary(this.annual_new_site_staff_salary()))
      / (this.days_worked_per_year() * this.hours_worked_per_day());
  }

  total_hours() { // ROI Analysis S46
    return this.time_spent_managing_billing()
      + this.time_spent_on_payment_collection()
      + this.time_spent_on_payment_reconciliation()
      + this.time_spent_processing_refunds()
      + this.time_spent_per_new_site() * this.new_sites_per_year();
  }

  total_hours_saved() { // Mockup R45
    return this.dollar_value_of_time_saved() / this.hourly_blended_salary();
  }

  // Static Inputs
  days_worked_per_year() {  // Configurator - K70
    return 236;
  }

  hours_worked_per_day() {  // Configurator - K74
    return 7;
  }

  merchant_service_monthly_fee() { // Benefit Calculations - N93
    return 0.02;
  }

  // Calculated Inputs
  amount_per_statement() { // K52
    return this.input.amount_per_statement;
  }

  // User Inputs
  time_spent_managing_billing() { // K22
    return this.input.time_spent_managing_billing;
  }

  number_of_offices() { // K16
    return this.input.number_of_offices;
  }

  time_spent_on_payment_collection() { // K26
    return this.input.time_spent_on_payment_collection;
  }

  time_spent_on_payment_reconciliation() { // K30
    return this.input.time_spent_on_payment_reconciliation;
  }

  time_spent_processing_refunds() { // K34
    return this.input.time_spent_processing_refunds;
  }

  new_sites_per_year() { // K39
    return this.input.new_sites_per_year;
  }

  processed_dollar_volume_per_month() { // K15
    return this.input.processed_dollar_volume_per_month;
  }

  systems_replaced_by_pmb() { // K44
    return this.input.systems_replaced_by_pmb;
  }

  patient_statements_per_month() { // K17
    return this.input.patient_statements_per_month;
  }

  collection_rate() { // K54
    return this.input.collection_rate;
  }

  // Investment Inputs

  effective_rate() { // Configurator - H43
    return this.input.effective_rate / 100;
  }

  hardware_cost_per_month() { // Configurator - H50
    return this.input.hardware_cost_per_month;
  }

  other_one_time_costs() {
    return this.input.other_one_time_costs;
  }

  other_yearly_costs() {
    return this.input.other_yearly_costs;
  }

  // Benefit Calculations

  // Benefit 1
  reduce_time_managing_patient_billing() {
    return this.time_spent_managing_billing() * 52 * this.number_of_offices()
      * this.fully_burdened_salary(this.annual_billing_staff_salary()) / this.days_worked_per_year()
    / this.hours_worked_per_day() * this.percent_reduction_in_time_spent_managing_billing();
  }

  // Benefit 2
  reduce_time_collecting_posting_payment() {
    return this.time_spent_on_payment_collection() * 52 * this.number_of_offices()
      * this.fully_burdened_salary(this.annual_payment_collecting_staff_salary()) / this.days_worked_per_year()
    / this.hours_worked_per_day() * this.percent_reduction_in_time_spent_on_payment_collection();
  }

  // Benefit 3
  reduce_time_reconciling_payment() {
    return this.time_spent_on_payment_reconciliation() * 52 * this.number_of_offices()
      * this.fully_burdened_salary(this.annual_payment_reconciliation_staff_salary())
    / this.days_worked_per_year() / this.hours_worked_per_day()
      * this.percent_reduction_in_time_spent_on_payment_reconciliation();
  }

  // Benefit 4
  reduce_time_processing_refunds() {
    return this.time_spent_processing_refunds() * 52 * this.number_of_offices()
      * this.fully_burdened_salary(this.annual_refund_processing_staff_salary())
    / this.days_worked_per_year() / this.hours_worked_per_day()
      * this.percent_reduction_in_time_spent_on_processing_refunds();
  }

  // Benefit 5
  reduce_time_on_new_sites() {
    return this.time_spent_per_new_site() * this.new_sites_per_year()
      * this.fully_burdened_salary(this.annual_new_site_staff_salary())
    / this.days_worked_per_year() / this.hours_worked_per_day()
      * this.percent_reduction_in_time_spent_on_new_sites();
  }

  // Benefit 6
  eliminate_cost_of_merchant_services() {
    // Disable this benefit for PMB. It makes more sense for the direct sales tool.
    return 0;
  }

  // Benefit 7
  consolidate_systems() {
    return this.systems_replaced_by_pmb() * this.spend_per_system() * this.number_of_offices()
      * this.systems_displaced_by_pmb();
  }

  // Benefit 8
  reduce_cost_of_compliance() {
    return (this.pci_compliance_fees_per_office() * this.number_of_offices()
      + (this.nonpci_compliance_fees_per_office() * 12) * this.number_of_offices())
    * this.percent_reduction_in_compliance_fees();
  }

  // Benefit 9
  improve_payment_collection() {
    const before = this.patient_statements_per_month()
      * this.amount_per_statement()
      * 12
      * this.collection_rate();

    const after = this.patient_statements_per_month()
      * this.amount_per_statement()
      * 12
      * (this.collection_rate() + this.percentage_point_improvement_in_collection());

    return after - before;
  }

  all_benefits() {
    return [
      'reduce_time_managing_patient_billing',
      'reduce_time_collecting_posting_payment',
      'reduce_time_reconciling_payment',
      'reduce_time_processing_refunds',
      'reduce_time_on_new_sites',
      // 'eliminate_cost_of_merchant_services',
      // 'consolidate_systems',
      // 'reduce_cost_of_compliance',
      'improve_payment_collection'
    ];
  }

  value_drivers() {
    return [
      'improve_operational_efficiency',
      // 'reduce_costs',
      'increase_collections'
    ];
  }

  improve_operational_efficiency() {
    return this.reduce_time_managing_patient_billing()
      +  this.reduce_time_collecting_posting_payment()
      +  this.reduce_time_reconciling_payment()
      +  this.reduce_time_processing_refunds()
      +  this.reduce_time_on_new_sites();
  }

  reduce_costs() {
    return this.eliminate_cost_of_merchant_services()
      + this.consolidate_systems()
      + this.reduce_cost_of_compliance();
  }

  increase_collections() {
    return this.improve_payment_collection();
  }

  adoption_rate_year_1() {
    return this.input.adoption_rate_year_1 / 100;
  }

  adoption_rate_year_2() {
    return this.input.adoption_rate_year_2 / 100;
  }

  adoption_rate_year_3() {
    return this.input.adoption_rate_year_3 / 100;
  }

  adoption_rate_year_4() {
    return this.input.adoption_rate_year_4 / 100;
  }

  adoption_rate_year_5() {
    return this.input.adoption_rate_year_5 / 100;
  }

  adoption_rate(year) {
    switch (year) {
      case 1: return this.adoption_rate_year_1();
      case 2: return this.adoption_rate_year_2();
      case 3: return this.adoption_rate_year_3();
      case 4: return this.adoption_rate_year_4();
      case 5: return this.adoption_rate_year_5();
      default: return 1.0;
    }
  }

  total_annual_investment(year = 1) {
    const recurring_costs = this.effective_rate() * this.processed_dollar_volume_per_month() * 12
      + this.other_yearly_costs();

      if (year > 1) {
        return recurring_costs;
      }

    // hardware_cost_per_month is now a one-time charge in year 1
    // https://www.pivotaltracker.com/story/show/188753242
    return recurring_costs
      + this.other_one_time_costs()
      + this.hardware_cost_per_month();
  }

  improve_operational_efficiency_label() {
    return this.input.improve_operational_efficiency_label;
  }

  reduce_costs_label() {
    return this.input.reduce_costs_label;
  }

  increase_collections_label() {
    return this.input.increase_collections_label;
  }

  reduce_time_managing_patient_billing_label() {
    return this.input.reduce_time_managing_patient_billing_label;
  }

  reduce_time_collecting_posting_payment_label() {
    return this.input.reduce_time_collecting_posting_payment_label;
  }

  reduce_time_reconciling_payment_label() {
    return this.input.reduce_time_reconciling_payment_label;
  }

  reduce_time_processing_refunds_label() {
    return this.input.reduce_time_processing_refunds_label;
  }

  reduce_time_on_new_sites_label() {
    return this.input.reduce_time_on_new_sites_label;
  }

  eliminate_cost_of_merchant_services_label() {
    return this.input.eliminate_cost_of_merchant_services_label;
  }

  consolidate_systems_label() {
    return this.input.consolidate_systems_label;
  }

  reduce_cost_of_compliance_label() {
    return this.input.reduce_cost_of_compliance_label;
  }

  improve_payment_collection_label() {
    return this.input.improve_payment_collection_label;
  }
}

export default PmbEnterprise;
